import React, { useState, useEffect } from 'react';
import { 
  FileCheck, FileText, ClipboardList, CheckCircle, Clock, Phone,
  ChevronDown, ArrowRight, Building2, Zap, Shield, Award, Users,
  AlertCircle, BookOpen, Scale
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const GestoriaPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: FileText,
      title: 'Trámites con ANDE',
      description: 'Gestionamos todos los permisos y autorizaciones necesarios con la Administración Nacional de Electricidad.',
      items: ['Solicitud de conexión', 'Medidor bidireccional', 'Contrato de inyección', 'Inspecciones técnicas']
    },
    {
      icon: Scale,
      title: 'Asesoría Legal',
      description: 'Te acompañamos en todos los aspectos legales y normativos de tu instalación solar.',
      items: ['Contratos de servicio', 'Documentación técnica', 'Cumplimiento normativo', 'Resolución de conflictos']
    },
    {
      icon: ClipboardList,
      title: 'Documentación Técnica',
      description: 'Preparamos toda la documentación técnica requerida por las autoridades.',
      items: ['Memoria técnica', 'Planos eléctricos', 'Certificaciones', 'Informes de capacidad']
    },
    {
      icon: Building2,
      title: 'Permisos Municipales',
      description: 'Tramitamos los permisos de construcción y habilitaciones municipales necesarias.',
      items: ['Permiso de obra', 'Habilitación municipal', 'Certificado de seguridad', 'Inspección final']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Evaluación Inicial',
      description: 'Analizamos tu situación actual y los requisitos específicos para tu instalación.'
    },
    {
      step: '02',
      title: 'Preparación de Documentos',
      description: 'Recopilamos y preparamos toda la documentación técnica y legal necesaria.'
    },
    {
      step: '03',
      title: 'Presentación a ANDE',
      description: 'Presentamos la solicitud formal y hacemos seguimiento del expediente.'
    },
    {
      step: '04',
      title: 'Coordinación de Inspecciones',
      description: 'Organizamos y acompañamos las inspecciones técnicas requeridas.'
    },
    {
      step: '05',
      title: 'Aprobación y Conexión',
      description: 'Obtenemos las aprobaciones finales y coordinamos la conexión a la red.'
    }
  ];

  const requirements = [
    'Documento de identidad del titular',
    'Título de propiedad o contrato de alquiler',
    'Última factura de ANDE',
    'Plano de ubicación del inmueble',
    'Especificaciones técnicas del sistema solar'
  ];

  const faqs = [
    {
      question: '¿Qué es la inyección a red?',
      answer: 'Es el proceso mediante el cual la energía excedente que genera tu sistema solar se envía a la red eléctrica de ANDE. Esta energía se descuenta de tu factura o, en algunos casos, puede generar un crédito a tu favor.'
    },
    {
      question: '¿Cuánto tiempo demora el trámite completo?',
      answer: 'El proceso completo de gestión con ANDE típicamente toma entre 30 y 60 días, dependiendo de la complejidad del proyecto y la carga de trabajo de las entidades. Nosotros hacemos seguimiento continuo para agilizar los tiempos.'
    },
    {
      question: '¿Necesito cambiar mi medidor?',
      answer: 'Sí, para inyectar energía a la red necesitás un medidor bidireccional que registre tanto la energía que consumís como la que inyectás. Este cambio lo coordina ANDE como parte del proceso de habilitación.'
    },
    {
      question: '¿Puedo hacer los trámites yo mismo?',
      answer: 'Técnicamente sí, pero el proceso es complejo y requiere conocimiento técnico y legal específico. Nuestra gestoría te ahorra tiempo, evita errores y garantiza que todo se haga correctamente a la primera.'
    },
    {
      question: '¿Qué pasa si ANDE rechaza mi solicitud?',
      answer: 'Los rechazos suelen deberse a documentación incompleta o errores técnicos. Nosotros nos aseguramos de que todo esté correcto antes de presentar, minimizando el riesgo de rechazo. Si ocurre, nos encargamos de las correcciones sin costo adicional.'
    },
    {
      question: '¿El servicio de gestoría está incluido en la instalación?',
      answer: 'En la mayoría de nuestros paquetes de instalación, la gestoría básica con ANDE está incluida. Para trámites adicionales o casos especiales, ofrecemos servicios de gestoría por separado.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
              <FileCheck className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Gestoría y Trámites</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Trámites de Inyección a Red
              <span className="text-[#90EE90]"> Sin Complicaciones</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
              Nos encargamos de toda la burocracia con ANDE y entidades reguladoras. 
              Vos solo preocupate por disfrutar tu energía solar.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/595991344956?text=Hola%20Zenex!%20Necesito%20ayuda%20con%20trámites%20de%20inyección%20a%20red"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
              >
                Consultar Ahora
                <ArrowRight size={20} />
              </a>
              <button
                onClick={() => navigate('/calculadora')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                Calcular Sistema
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Nuestros Servicios
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Gestoría Integral
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Todo lo que necesitás para conectar tu sistema solar a la red de forma legal y segura
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white p-6 sm:p-8 rounded-2xl border border-zinc-200 hover:shadow-xl transition-all duration-300">
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-3">{service.title}</h3>
                  <p className="text-zinc-600 text-sm mb-4">{service.description}</p>
                  <ul className="grid grid-cols-2 gap-2">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                        <CheckCircle className="w-4 h-4 text-[#90EE90] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              El Proceso
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              ¿Cómo Funciona?
            </h2>
          </div>

          <div className="relative">
            {/* Connection line - desktop */}
            <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-zinc-200"></div>
            
            <div className="grid md:grid-cols-5 gap-6">
              {process.map((item, index) => (
                <div key={index} className="relative text-center">
                  <div className="relative z-10 w-16 h-16 bg-[#90EE90] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <span className="text-xl font-bold text-zinc-900">{item.step}</span>
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-2 text-sm">{item.title}</h3>
                  <p className="text-xs text-zinc-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-2 bg-zinc-100 px-6 py-3 rounded-full">
              <Clock className="w-5 h-5 text-zinc-600" />
              <span className="text-zinc-700 font-medium">Tiempo estimado: 30-60 días</span>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
                Requisitos
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                ¿Qué Documentos Necesito?
              </h2>
              <p className="text-zinc-400 mb-8">
                Para iniciar el trámite de inyección a red, necesitás tener a mano los siguientes documentos. 
                No te preocupes, te guiamos en cada paso.
              </p>
              
              <ul className="space-y-3">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="w-5 h-5 text-[#90EE90] flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-zinc-800 p-6 sm:p-8 rounded-2xl border border-zinc-700">
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="w-6 h-6 text-amber-400" />
                <h3 className="text-lg font-bold text-white">Importante</h3>
              </div>
              <ul className="space-y-4 text-zinc-300 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-[#90EE90] font-bold">•</span>
                  La instalación debe cumplir con las normas técnicas de ANDE para poder inyectar a la red.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#90EE90] font-bold">•</span>
                  El sistema debe ser instalado por personal certificado con los equipos homologados.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#90EE90] font-bold">•</span>
                  Si contratás tu sistema con Zenex, la gestoría básica está incluida.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Sobre Inyección a Red
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#14532D] to-[#166534]">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <FileCheck className="w-12 h-12 text-[#90EE90] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Necesitás Ayuda con los Trámites?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Consultanos sin compromiso. Te asesoramos sobre todo el proceso de inyección a red.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Necesito%20asesoramiento%20sobre%20trámites%20con%20ANDE"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-white text-zinc-900 font-bold rounded-xl hover:bg-zinc-100 transition-colors inline-flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Hablar con un Asesor
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default GestoriaPage;
